import React, { useMemo, useState, useEffect } from "react";
import {
  ArrowDown,
  ArrowUp,
  Download,
  Filter,
  Globe,
  Package,
  Plus,
  Search,
  Settings2,
  ShoppingBag,
  Tag,
  User,
  X,
} from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";
import B2CHeader from "./B2CHeader";
import { Badge, Card, FieldBase, PrimaryButton, SecondaryButton, SelectBase, cx } from "./b2cUi";

// Mock data - replace with actual API call
const MOCK_PRICING_RULES = [
  {
    id: 1,
    name: "US Premium Pricing",
    type: "country",
    target: "United States",
    condition: "country = 'US'",
    action: "markup",
    value: 15,
    status: "active",
    priority: 1,
    createdAt: "2024-01-01",
    updatedAt: "2024-01-15",
  },
  {
    id: 2,
    name: "VIP Customer Discount",
    type: "customer",
    target: "VIP Customers",
    condition: "customer_tier = 'VIP'",
    action: "discount",
    value: 10,
    status: "active",
    priority: 2,
    createdAt: "2024-01-05",
    updatedAt: "2024-01-10",
  },
  {
    id: 3,
    name: "Bulk Order Discount",
    type: "quantity",
    target: "Bulk Orders",
    condition: "quantity >= 10",
    action: "discount",
    value: 20,
    status: "active",
    priority: 3,
    createdAt: "2024-01-08",
    updatedAt: "2024-01-12",
  },
  {
    id: 4,
    name: "EU VAT Adjustment",
    type: "country",
    target: "European Union",
    condition: "country IN ('DE', 'FR', 'IT', 'ES')",
    action: "markup",
    value: 8,
    status: "inactive",
    priority: 4,
    createdAt: "2024-01-10",
    updatedAt: "2024-01-14",
  },
  {
    id: 5,
    name: "New Customer Welcome",
    type: "customer",
    target: "First-time Buyers",
    condition: "order_count = 1",
    action: "discount",
    value: 5,
    status: "active",
    priority: 5,
    createdAt: "2024-01-12",
    updatedAt: "2024-01-15",
  },
];

const PricingManagement = () => {
  const [pricingRules, setPricingRules] = useState([]);
  const [filteredRules, setFilteredRules] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  useEffect(() => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setPricingRules(MOCK_PRICING_RULES);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    let filtered = pricingRules;

    // Filter by search term
    if (searchFilter) {
      filtered = filtered.filter(rule =>
        rule.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        rule.target.toLowerCase().includes(searchFilter.toLowerCase()) ||
        rule.condition.toLowerCase().includes(searchFilter.toLowerCase())
      );
    }

    // Filter by type
    if (typeFilter !== "all") {
      filtered = filtered.filter(rule => rule.type === typeFilter);
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(rule => rule.status === statusFilter);
    }

    setFilteredRules(filtered);
  }, [pricingRules, searchFilter, typeFilter, statusFilter]);

  const handleCreateRule = () => {
    setShowCreateModal(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setShowCreateModal(true);
  };

  const handleDeleteRule = (ruleId) => {
    if (window.confirm("Are you sure you want to delete this pricing rule?")) {
      setPricingRules(prev => prev.filter(rule => rule.id !== ruleId));
    }
  };

  const handleToggleStatus = (ruleId) => {
    setPricingRules(prev =>
      prev.map(rule =>
        rule.id === ruleId
          ? { ...rule, status: rule.status === 'active' ? 'inactive' : 'active' }
          : rule
      )
    );
  };

  const statusTone = (status) => {
    const map = {
      active: "green",
      inactive: "rose",
      draft: "yellow",
    };
    return map[status] || "slate";
  };

  const getTypeIcon = (type) => {
    const map = {
      country: Globe,
      customer: User,
      quantity: Package,
      category: Tag,
      product: ShoppingBag,
    };
    return map[type] || Settings2;
  };

  const getActionColor = (action) => {
    return action === 'discount' ? 'text-green-600' : 'text-red-600';
  };

  const activeCount = useMemo(() => pricingRules.filter((r) => r.status === "active").length, [pricingRules]);
  const countryCount = useMemo(() => pricingRules.filter((r) => r.type === "country").length, [pricingRules]);
  const avgImpact = useMemo(() => {
    if (pricingRules.length === 0) return 0;
    return pricingRules.reduce((sum, rule) => sum + (Number(rule.value) || 0), 0) / pricingRules.length;
  }, [pricingRules]);

  return (
    <AdminPageShell
      title="Pricing Rules"
      description="Create and manage dynamic pricing rules for B2C customers. (Mock data)"
      chips={
        <div className="flex flex-wrap items-center gap-2">
          <Badge>
            <Settings2 className="h-4 w-4" />
            {pricingRules.length} rules
          </Badge>
          <Badge tone="green">{activeCount} active</Badge>
          <Badge tone="purple">
            <Globe className="h-4 w-4" />
            {countryCount} country
          </Badge>
          <Badge tone="gray">{avgImpact.toFixed(1)}% avg impact</Badge>
        </div>
      }
      actions={
        <>
          <SecondaryButton
            type="button"
            onClick={() => {
              setSearchFilter("");
              setTypeFilter("all");
              setStatusFilter("all");
            }}
          >
            Reset
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleCreateRule}
          >
            <Plus className="h-4 w-4" />
            Create Rule
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-6">
        <B2CHeader />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center">
                <Settings2 className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Total Rules</p>
                <p className="text-xl font-black text-slate-900">{pricingRules.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-emerald-200 bg-emerald-50 flex items-center justify-center">
                <Badge tone="green" className="px-2 py-1">
                  Active
                </Badge>
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Active Rules</p>
                <p className="text-xl font-black text-slate-900">{activeCount}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-violet-200 bg-violet-50 flex items-center justify-center">
                <Globe className="h-5 w-5 text-violet-900" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Country Rules</p>
                <p className="text-xl font-black text-slate-900">{countryCount}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-white flex items-center justify-center">
                <span className="text-sm font-black text-slate-700">%</span>
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Avg Impact</p>
                <p className="text-xl font-black text-slate-900">{avgImpact.toFixed(1)}%</p>
              </div>
            </div>
          </Card>
        </div>

        <Card className="p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <h3 className="text-sm font-black text-slate-900">Quick Actions</h3>
            <SecondaryButton type="button" onClick={handleCreateRule}
            >
              <Plus className="h-4 w-4" />
              Create New Rule
            </SecondaryButton>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button type="button" className="text-left">
              <Card className="p-4 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-white flex items-center justify-center">
                    <Globe className="h-5 w-5 text-slate-700" />
                  </div>
                  <div>
                    <p className="font-black text-slate-900">Country-Based Pricing</p>
                    <p className="text-sm text-slate-600">Set different prices by country</p>
                  </div>
                </div>
              </Card>
            </button>

            <button type="button" className="text-left">
              <Card className="p-4 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-white flex items-center justify-center">
                    <User className="h-5 w-5 text-slate-700" />
                  </div>
                  <div>
                    <p className="font-black text-slate-900">Customer Tier Pricing</p>
                    <p className="text-sm text-slate-600">VIP, Premium, Regular pricing</p>
                  </div>
                </div>
              </Card>
            </button>

            <button type="button" className="text-left">
              <Card className="p-4 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-white flex items-center justify-center">
                    <Package className="h-5 w-5 text-slate-700" />
                  </div>
                  <div>
                    <p className="font-black text-slate-900">Volume Discounts</p>
                    <p className="text-sm text-slate-600">Bulk order pricing rules</p>
                  </div>
                </div>
              </Card>
            </button>
          </div>
        </Card>

        <Card className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Name, target, condition"
                  className={cx(FieldBase, "pl-10")}
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Type</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <select
                  className={cx(SelectBase, "pl-10")}
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="all">All types</option>
                  <option value="country">Country</option>
                  <option value="customer">Customer</option>
                  <option value="quantity">Quantity</option>
                  <option value="category">Category</option>
                  <option value="product">Product</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Status</label>
              <select className={SelectBase} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <div className="flex items-end">
              <SecondaryButton type="button" className="w-full justify-center" onClick={() => alert("Export coming soon")}
              >
                <Download className="h-4 w-4" />
                Export
              </SecondaryButton>
            </div>
          </div>
        </Card>

        <Card className="overflow-hidden">
          <div className="p-5 border-b border-slate-200">
            <h3 className="text-sm font-black text-slate-900">Pricing Rules ({filteredRules.length})</h3>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto" />
              <p className="mt-2 text-slate-600">Loading pricing rules…</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Rule</th>
                      <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Type & Target</th>
                      <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Action</th>
                      <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Priority</th>
                      <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {filteredRules.map((rule) => {
                    const TypeIcon = getTypeIcon(rule.type);

                    return (
                      <tr key={rule.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">{rule.name}</div>
                            <div className="text-sm text-slate-600">{rule.condition}</div>
                            <div className="text-xs text-slate-500">Updated: {new Date(rule.updatedAt).toLocaleDateString()}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <TypeIcon className="h-4 w-4 text-slate-700 mr-2" />
                            <div>
                              <div className="text-sm font-semibold text-slate-900 capitalize">{rule.type}</div>
                              <div className="text-sm text-slate-600">{rule.target}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={cx("text-sm font-semibold capitalize flex items-center gap-2", getActionColor(rule.action))}>
                            {rule.action === "discount" ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
                            {rule.value}%
                          </div>
                          <div className="text-sm text-slate-600 capitalize">{rule.action}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge tone="blue">Priority {rule.priority}</Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge tone={statusTone(rule.status)}>
                            {rule.status.charAt(0).toUpperCase() + rule.status.slice(1)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex flex-wrap items-center gap-2">
                            <SecondaryButton type="button" className="px-3 py-1.5 text-xs" onClick={() => handleEditRule(rule)}>
                              Edit
                            </SecondaryButton>
                            <SecondaryButton
                              type="button"
                              className={cx(
                                "px-3 py-1.5 text-xs",
                                rule.status === "active"
                                  ? "border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100"
                                  : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                              )}
                              onClick={() => handleToggleStatus(rule.id)}
                            >
                              {rule.status === "active" ? "Deactivate" : "Activate"}
                            </SecondaryButton>
                            <SecondaryButton
                              type="button"
                              className="px-3 py-1.5 text-xs border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100"
                              onClick={() => handleDeleteRule(rule.id)}
                            >
                              Delete
                            </SecondaryButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {filteredRules.length === 0 && !loading ? (
            <div className="p-8 text-center">
              <p className="text-slate-600">No pricing rules found matching your criteria.</p>
              <PrimaryButton type="button" className="mt-4" onClick={handleCreateRule}
              >
                <Plus className="h-4 w-4" />
                Create Your First Rule
              </PrimaryButton>
            </div>
          ) : null}
        </Card>
      </div>

      {showCreateModal ? (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 w-full max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-black text-slate-900">
                {editingRule ? "Edit Pricing Rule" : "Create New Pricing Rule"}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingRule(null);
                }}
                className="inline-flex items-center justify-center h-9 w-9 rounded-2xl border border-slate-200 hover:bg-slate-50"
              >
                <X className="h-4 w-4 text-slate-700" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Rule Name</label>
                <input
                  type="text"
                  className={FieldBase}
                  placeholder="Enter rule name"
                  defaultValue={editingRule?.name || ""}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Type</label>
                  <select className={SelectBase} defaultValue={editingRule?.type || "country"}>
                    <option value="country">Country</option>
                    <option value="customer">Customer</option>
                    <option value="quantity">Quantity</option>
                    <option value="category">Category</option>
                    <option value="product">Product</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Action</label>
                  <select className={SelectBase} defaultValue={editingRule?.action || "discount"}>
                    <option value="discount">Discount</option>
                    <option value="markup">Markup</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Value (%)</label>
                <input
                  type="number"
                  className={FieldBase}
                  placeholder="Enter percentage value"
                  defaultValue={editingRule?.value || ""}
                />
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Condition</label>
                <textarea
                  className={cx(
                    "w-full min-h-[96px] resize-y rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30"
                  )}
                  rows={3}
                  placeholder="Enter condition (e.g., country = 'US')"
                  defaultValue={editingRule?.condition || ""}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <SecondaryButton
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingRule(null);
                }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton type="button" onClick={() => alert("Save coming soon")}
              >
                {editingRule ? "Update Rule" : "Create Rule"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}
    </AdminPageShell>
  );
};

export default PricingManagement;
